import { Check, RotateCcw, Save, Shield } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { Heading } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import {
  GetPermissionListResultDto,
  PermissionGrantInfoDto,
  PermissionGroupDto,
  UpdatePermissionDto
} from '@unistocks/proxy/axios-client';
import {
  permissionsGetQueryKey,
  usePermissionsGetQuery,
  usePermissionsUpdateMutation
} from '@unistocks/proxy/axios-client/PermissionsQuery';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { t } from '@unistocks/shared/localization/index.ts';
import React, { useState } from 'react';
import { Checkbox, Label, Separator } from 'tamagui';

type PermissionManagementSheetProps = {
  providerName: string;
  providerKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type PermissionState = Record<string, boolean>;

const selectPermissions = (data: GetPermissionListResultDto) => {
  const initialState: PermissionState = {};
  data.groups?.forEach((group: PermissionGroupDto) => {
    group.permissions?.forEach((permission: PermissionGrantInfoDto) => {
      initialState[permission.name!] = permission.isGranted;
    });
  });
  return { groups: data.groups, initialState };
};

export const PermissionManagementSheet: React.FC<PermissionManagementSheetProps> = ({
  providerName,
  providerKey,
  open,
  onOpenChange
}) => {
  const queryClient = useQueryClient();
  const [permissionState, setPermissionState] = useState<PermissionState>({});

  const { data, isPending } = usePermissionsGetQuery(providerName, providerKey, {
    enabled: open,
    select: selectPermissions
  });

  const { mutateAsync: updatePermissions, isPending: isSaving } = usePermissionsUpdateMutation(
    providerName,
    providerKey,
    {
      onSuccess: () => {
        toast.success(t('AbpUi::SavedSuccessfully'));
        queryClient.invalidateQueries({
          queryKey: permissionsGetQueryKey(providerName, providerKey)
        });
        queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
        onOpenChange(false);
      }
    }
  );

  const [prevData, setPrevData] = useState(data);

  if (prevData !== data) {
    setPrevData(data);

    if (data?.initialState) {
      setPermissionState(data.initialState);
    }
  }

  const togglePermission = (name: string, isGranted: boolean) => {
    setPermissionState(prev => {
      const next = { ...prev, [name]: isGranted };

      // If unchecking a parent, uncheck all children
      if (!isGranted) {
        data?.groups?.forEach((group: PermissionGroupDto) => {
          group.permissions?.forEach((perm: PermissionGrantInfoDto) => {
            if (perm.parentName === name) {
              next[perm.name!] = false;
            }
          });
        });
      }

      // If checking a child, check parent
      if (isGranted) {
        data?.groups?.forEach((group: PermissionGroupDto) => {
          const permission = group.permissions?.find(
            (p: PermissionGrantInfoDto) => p.name === name
          );
          if (permission?.parentName) {
            next[permission.parentName] = true;
          }
        });
      }

      return next;
    });
  };

  const toggleAll = (isGranted: boolean) => {
    setPermissionState(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        next[key] = isGranted;
      });
      return next;
    });
  };

  const handleSave = () => {
    const permissions: UpdatePermissionDto[] = Object.entries(permissionState).map(
      ([name, isGranted]) => ({ name, isGranted })
    );
    updatePermissions({ permissions });
  };

  return (
    <FullScreenSheet
      open={open}
      onOpenChange={onOpenChange}
      headerTitle={t('AbpPermissionManagement::Permissions')}
      headerIcon={<Shield color={'$unistocks'} />}
    >
      {isPending ? (
        <Loader mt={'$5'} />
      ) : (
        <Column gap={'$3'}>
          <Row alignItems="center">
            <Checkbox
              id="select-all"
              checked={Object.values(permissionState).every(Boolean)}
              onCheckedChange={checked => toggleAll(!!checked)}
            >
              <Checkbox.Indicator>
                <Check />
              </Checkbox.Indicator>
            </Checkbox>
            <Label htmlFor="select-all" fontWeight={'bold'}>
              {t('AbpPermissionManagement::SelectAllInAllTabs')}
            </Label>
          </Row>

          <Separator />

          {data?.groups?.map(group => (
            <Column key={group.name} gap={'$2'}>
              <Heading size={'$6'}>{group.displayName}</Heading>
              <Separator />

              {group.permissions?.map(permission => (
                <Row
                  key={permission.name}
                  alignItems="center"
                  pl={permission.parentName ? '$4' : undefined}
                >
                  <Checkbox
                    id={`perm-${permission.name}`}
                    checked={permissionState[permission.name!] ?? false}
                    onCheckedChange={checked => togglePermission(permission.name!, !!checked)}
                  >
                    <Checkbox.Indicator>
                      <Check />
                    </Checkbox.Indicator>
                  </Checkbox>
                  <Label htmlFor={`perm-${permission.name}`}>{permission.displayName}</Label>
                </Row>
              ))}
            </Column>
          ))}

          <Separator my={'$2'} />

          <SubmitButtons
            cancelButtonText={t('AbpUi::Cancel')}
            cancelButtonIcon={RotateCcw}
            onCancel={() => onOpenChange(false)}
            submitButtonText={t('AbpUi::Save')}
            submitButtonIcon={Save}
            onSubmit={handleSave}
            isSubmitting={isSaving}
          />
        </Column>
      )}
    </FullScreenSheet>
  );
};

PermissionManagementSheet.displayName = 'PermissionManagementSheet';
